import React, { useEffect, useState } from 'react';
import { CheckCircle, Package, ArrowRight, Sparkles } from 'lucide-react';
import { formatPrice } from '../utils';

interface OrderSuccessProps {
  isOpen: boolean;
  onClose: () => void;
  orderDetails: {
    orderId: string;
    amount: number;
    items: { name: string; quantity: number }[];
  } | null;
}

const OrderSuccess: React.FC<OrderSuccessProps> = ({ isOpen, onClose, orderDetails }) => {
  const [showContent, setShowContent] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<{ id: number; left: number; delay: number; color: string; size: number; duration: number }[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Generate confetti
      const pieces = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.8,
        color: ['#a855f7', '#ec4899', '#8b5cf6', '#d946ef', '#f472b6', '#c084fc', '#ffffff'][Math.floor(Math.random() * 7)],
        size: Math.random() * 8 + 4,
        duration: Math.random() * 2 + 2,
      }));
      setConfettiPieces(pieces);

      // Stagger reveal
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      setConfettiPieces([]);
    }
  }, [isOpen]);

  if (!isOpen || !orderDetails) return null;

  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl flex items-center justify-center animate-fade-in overflow-hidden">
      
      {/* Confetti */}
      {confettiPieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute top-0 pointer-events-none"
          style={{
            left: `${piece.left}%`,
            width: `${piece.size}px`,
            height: `${piece.size * 1.5}px`,
            backgroundColor: piece.color,
            borderRadius: '2px',
            animation: `confettiFall ${piece.duration}s ease-in ${piece.delay}s forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-600/20 rounded-full blur-[150px] pointer-events-none" />

      {/* Content */}
      <div className={`relative text-center max-w-lg mx-auto px-6 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        
        {/* Animated Checkmark */}
        <div className="relative mx-auto w-24 h-24 mb-8">
          <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute inset-0 bg-green-500/10 rounded-full" />
          <div className="relative w-full h-full bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/30">
            <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
          </div>
        </div>

        {/* Heading */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h1 className="text-xs font-black uppercase tracking-[0.4em] text-amber-400">Order Confirmed</h1>
          <Sparkles className="w-5 h-5 text-amber-400" />
        </div>
        <p className="text-4xl md:text-5xl font-black tracking-tighter mb-8">
          Thank You<span className="text-amber-500">.</span>
        </p>

        {/* Order Card */}
        <div className="bg-neutral-900/80 border border-white/10 rounded-2xl p-6 mb-8 text-left backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/5">
            <Package className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest">Order ID</p>
              <p className="text-xs font-mono font-bold text-neutral-300">{orderDetails.orderId}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest">Amount Paid</p>
              <p className="text-lg font-black text-amber-500 tracking-tight">{formatPrice(orderDetails.amount / 100)}</p>
            </div>
          </div>

          {/* Items */}
          <div className="space-y-2">
            <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest mb-2">Items</p>
            {orderDetails.items.map((item, idx) => (
              <div key={idx} className="flex justify-between text-[10px] font-bold uppercase tracking-wide">
                <span className="text-neutral-400">{item.name}</span>
                <span className="text-neutral-300">× {item.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-neutral-500 mb-2">
          A confirmation email has been sent to your inbox.
        </p>
        <p className="text-xs text-amber-400/70 mb-6 flex items-center justify-center gap-1.5">
          <span>🚚</span> Your order is being processed for shipment. Track it from <span className="font-bold underline underline-offset-2">My Orders</span>.
        </p>

        {/* CTA */}
        <button
          onClick={onClose}
          className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-amber-600 hover:text-white transition-all duration-300"
        >
          Continue Shopping
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Confetti keyframes */}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default OrderSuccess;
